import {
  Input,
  InputProps,
  DateRangePickerProps,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  DateRangePicker,
  Popover,
  StatusIndicator,
  SpaceBetween,
  Alert,
} from "@amzn/awsui-components-console";
import React, { useEffect, useMemo, useState } from "react";
import { RELATIVE_OPTIONS, RELATIVE_RANGE_STRINGS } from "../searchResults/constants";
import { SEARCH_TERM_RULES } from "src/claimsHistory/searchTermRules";
import type { SearchableField, TermNormalization } from "src/claimsHistory/types";

export interface ClaimsSearchBarProps {
  readonly fields: readonly SearchableField[];
  readonly selectedFieldId: string;
  readonly term: string;
  readonly dateRange: DateRangePickerProps.Value | null;
  readonly errorMessage?: string | React.ReactNode;
  readonly onFieldChange: (fieldId: string) => void;
  readonly onTermChange: (term: string) => void;
  readonly onDateRangeChange: (value: DateRangePickerProps.Value | null) => void;
  readonly onSubmit: () => void;
  readonly onClearError: () => void;
}

const EMPTY_TERM_MESSAGE = "Search term cannot be empty. Please verify and try again.";

const overLengthMessage = (maxTermLength: number) =>
  `Search term cannot be greater than ${maxTermLength} characters. Please verify and try again.`;

/**
 * Tracks a min-width media query so the bar can respond to screen size. The
 * listener is registered on the MediaQueryList rather than on window resize so
 * a match change fires once, at the boundary.
 */
const useMediaQuery = (query: string) => {
  const mediaQuery = useMemo(() => window.matchMedia(query), [query]);
  const [match, setMatch] = useState(mediaQuery.matches);

  useEffect(() => {
    const onChange = () => setMatch(mediaQuery.matches);
    mediaQuery.addEventListener("change", onChange);

    return () => mediaQuery.removeEventListener("change", onChange);
  }, [mediaQuery]);

  return match;
};

const applyNormalization = (value: string, normalization?: TermNormalization) => {
  switch (normalization) {
    case "lowercase":
      return value.toLowerCase();
    case "uppercase":
      return value.toUpperCase();
    default:
      return value;
  }
};

const ClaimsSearchBar = ({
  fields,
  selectedFieldId,
  term,
  dateRange,
  errorMessage,
  onFieldChange,
  onTermChange,
  onDateRangeChange,
  onSubmit,
  onClearError,
}: ClaimsSearchBarProps) => {
  const [alertText, setAlertText] = useState<string | React.ReactNode>("");

  // One boundary for both label pairs: the button text and the input
  // placeholder switch together at 690px.
  const isWideViewport = useMediaQuery("(min-width: 690px)");

  const selectedField = fields.find((field) => field.fieldId === selectedFieldId);

  const otherFieldItems: ButtonDropdownProps.Item[] = useMemo(
    () =>
      fields
        .filter((field) => field.fieldId !== selectedFieldId)
        .map((field) => ({
          id: field.fieldId,
          text: isWideViewport ? field.labels.buttonText : field.labels.buttonTextSmall,
        })),
    [fields, selectedFieldId, isWideViewport]
  );

  useEffect(() => {
    setAlertText(errorMessage ?? "");
  }, [errorMessage]);

  if (!selectedField) {
    console.warn(`Search configuration not found for ${selectedFieldId}`);
    return null;
  }

  const searchDisabled = !term;

  const handleTermChange = (value: string) => {
    // Truncating here is what holds the term at the declared maximum, so an
    // over-length term can only arrive from outside this component.
    const normalized = applyNormalization(value, selectedField.normalization);
    onTermChange(normalized.slice(0, selectedField.maxTermLength));
  };

  const handleSubmit = () => {
    // Trimmed for the emptiness test only, not for what gets searched: the term
    // is the parent's state, and rewriting it here would land after this submit
    // for a parent that reads its own state. A whitespace-only term would
    // otherwise pass every check below and search for nothing.
    if (!term.trim()) {
      setAlertText(EMPTY_TERM_MESSAGE);
      return;
    }
    if (term.length > selectedField.maxTermLength) {
      setAlertText(overLengthMessage(selectedField.maxTermLength));
      return;
    }

    const violatedRule = (selectedField.validationRules ?? []).find(
      (rule) => !SEARCH_TERM_RULES[rule.ruleId](term)
    );
    if (violatedRule) {
      setAlertText(violatedRule.message);
      return;
    }

    setAlertText("");
    onSubmit();
  };

  const handleFieldChange = (fieldId: string) => {
    setAlertText("");
    onClearError();
    onTermChange("");
    // Clearing the range before reporting the field is load-bearing, not
    // incidental ordering. A parent may re-seed a range for the incoming field,
    // and both writes land in this one React event, so the parent's write has
    // to be the later one to survive. Reversing these two lines silently leaves
    // such a field unbounded.
    onDateRangeChange(null);
    onFieldChange(fieldId);
  };

  const onKeyDown = (key: string) => {
    if (key === "Enter" && !searchDisabled) {
      handleSubmit();
    }
  };

  const renderBarInput = () => (
    <div className="input-filter" id="input-filter">
      <Input
        data-testid="search-bar"
        clearAriaLabel="clear search bar"
        onKeyDown={({ detail }: { detail: InputProps.KeyDetail }) => onKeyDown(detail.key)}
        value={term}
        placeholder={
          isWideViewport ? selectedField.labels.placeholder : selectedField.labels.placeholderSmall
        }
        type="search"
        onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
          handleTermChange(detail.value)
        }
      />
    </div>
  );

  const renderSearchByControl = () => {
    const buttonText = isWideViewport
      ? selectedField.labels.buttonText
      : selectedField.labels.buttonTextSmall;

    return (
      <div className="search-by-button">
        {fields.length === 1 ? (
          <Button data-testid="search-by-button" onClick={handleSubmit}>
            {buttonText}
          </Button>
        ) : (
          <ButtonDropdown
            data-testid="search-by-button"
            items={otherFieldItems}
            expandableGroups
            onItemClick={({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) =>
              handleFieldChange(detail.id)
            }
            mainAction={{
              text: buttonText,
              onClick: handleSubmit,
            }}
          ></ButtonDropdown>
        )}
      </div>
    );
  };

  const renderDateRangePicker = () => {
    if (!selectedField.acceptsDateRange) {
      return null;
    }
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <DateRangePicker
          data-testid="search-bar-date-picker"
          onChange={({ detail }) => {
            onDateRangeChange(detail.value);
            handleSubmit();
          }}
          dateOnly={true}
          value={dateRange as DateRangePickerProps.Value}
          relativeOptions={RELATIVE_OPTIONS}
          i18nStrings={{
            formatRelativeRange: (value: DateRangePickerProps.RelativeValue) => {
              const preloadedRelativeString = value.key ? RELATIVE_RANGE_STRINGS[value.key] : "";
              if (preloadedRelativeString) {
                return preloadedRelativeString;
              }
              return `Past ${value.amount} ${value.unit}${value.amount > 1 ? "s" : ""}`;
            },
            relativeModeTitle: "Relative Date Ranges",
            absoluteModeTitle: "Specific Date Range",
          }}
          isValidRange={(range) => {
            if (!range) {
              return {
                valid: false,
                errorMessage: "Invalid selection. Please select a relative or specific date range.",
              };
            }
            if (range?.type === "relative") {
              if (!range.amount) {
                return {
                  valid: false,
                  errorMessage: "Invalid selection. Please select an amount for your custom range.",
                };
              }
            }
            if (range?.type === "absolute") {
              const [startDateWithoutTime] = range.startDate.split("T");
              const [endDateWithoutTime] = range.endDate.split("T");
              if (!startDateWithoutTime || !endDateWithoutTime) {
                return {
                  valid: false,
                  errorMessage:
                    "The selected date range is incomplete. Select a start and end date for the date range.",
                };
              }
              if (Date.parse(range.startDate) > Date.parse(range.endDate)) {
                return {
                  valid: false,
                  errorMessage:
                    "The selected date range is invalid. The start date must be before the end date.",
                };
              }
              const nextMidnight = new Date();
              nextMidnight.setHours(24, 0, 0, 0);
              const nextMidnightUTC = nextMidnight.getTime();
              if (
                Date.parse(range.startDate) > nextMidnightUTC ||
                Date.parse(range.endDate) > nextMidnightUTC
              ) {
                return {
                  valid: false,
                  errorMessage:
                    "The selected date range is invalid. Dates cannot be in the future.",
                };
              }
            }
            return { valid: true };
          }}
          placeholder="Filter by a date range"
        />
        <Popover
          header="Filter by submitted date"
          content="Filter the search results by selecting a relative date (past month, past year, etc.) or a specific date range."
          triggerType="custom"
          data-testid="claims-search-bar-date-range-popover"
        >
          <StatusIndicator type="info" />
        </Popover>
      </SpaceBetween>
    );
  };

  const renderAlert = () => {
    if (!alertText) return null;
    return (
      <div className="search-bar-alert">
        <Alert
          dismissible={true}
          type="error"
          statusIconAriaLabel={"Error"}
          dismissAriaLabel={"Close Alert"}
          key="search-bar-alert"
          onDismiss={() => {
            setAlertText("");
            onClearError();
          }}
        >
          {alertText}
        </Alert>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="claims-search-bar flex-wrap">
        {renderBarInput()}
        {renderSearchByControl()}
        {renderDateRangePicker()}
      </div>
      {renderAlert()}
    </React.Fragment>
  );
};
export default ClaimsSearchBar;
